import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { VehicleTypesTable } from "@/components/vehicle-types/VehicleTypesTable";
import { Skeleton } from "@/components/ui/skeleton";

interface VehicleType {
  id: string;
  name_ar: string;
  name_en: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export default function VehicleTypes() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<VehicleType | null>(null);
  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    sort_order: 1
  });
  const [view, setView] = useState<'table' | 'cards'>('cards');
  const queryClient = useQueryClient();

  const { data: vehicleTypes = [], isLoading } = useQuery({
    queryKey: ['vehicle-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_types')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as VehicleType[];
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase
        .from('vehicle_types')
        .insert([data]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] });
      toast.success("تم إضافة نوع السيارة بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء إضافة نوع السيارة");
      console.error(error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase
        .from('vehicle_types')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] });
      toast.success("تم تحديث نوع السيارة بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء تحديث نوع السيارة");
      console.error(error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('vehicle_types')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] });
      toast.success("تم حذف نوع السيارة بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء حذف نوع السيارة");
      console.error(error);
    }
  });

  const resetForm = () => {
    setFormData({ name_ar: "", name_en: "", sort_order: 1 });
    setEditingItem(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (item: VehicleType) => {
    setEditingItem(item);
    setFormData({
      name_ar: item.name_ar,
      name_en: item.name_en,
      sort_order: item.sort_order
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("هل أنت متأكد من حذف هذا النوع؟")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">أنواع السيارات</h1>
        <div className="flex items-center gap-4">
          <ViewToggle view={view} onViewChange={setView} />
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingItem(null);
                setFormData({ name_ar: "", name_en: "", sort_order: 1 });
              }}>
                <Plus className="w-4 h-4 mr-2" />
                إضافة نوع جديد
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "تعديل نوع السيارة" : "إضافة نوع سيارة جديد"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name_ar">الاسم بالعربية</Label>
                  <Input
                    id="name_ar"
                    value={formData.name_ar}
                    onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
                  <Input
                    id="name_en"
                    value={formData.name_en}
                    onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="sort_order">الترتيب</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 1 })}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    إلغاء
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                    {createMutation.isPending || updateMutation.isPending ? 'جاري الحفظ...' : (editingItem ? "تحديث" : "إضافة")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isLoading ? (
        view === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-[180px] w-full" />)}
          </div>
        ) : (
          <Skeleton className="h-[300px] w-full rounded-lg" />
        )
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicleTypes.map((type) => (
            <Card key={type.id}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>{type.name_ar}</span>
                  <span className="text-sm text-gray-500">#{type.sort_order}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{type.name_en}</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleEdit(type)}>
                    تعديل
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => handleDelete(type.id)}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? 'جاري الحذف...' : 'حذف'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <VehicleTypesTable vehicleTypes={vehicleTypes} onEdit={handleEdit} onDelete={handleDelete} />
      )}
      
      {vehicleTypes.length === 0 && !isLoading && (
        <div className="text-center py-12 text-gray-500">
          <p>لم يتم العثور على أي أنواع سيارات.</p>
        </div>
      )}
    </div>
  );
}
